// The day close's controls (F-OPS-06), kept apart from the report so the business-day lifecycle can change without
// touching how the day reads: the Close day action, the confirmation that says what closing does, the notices
// (a refused close, shifts still open) and the closed banner. Everything the server decides — whether the day may
// close, and the problem when it may not — arrives in the review or the refusal; this only asks and shows.
//
// ADR-0029: how the day ends is `shifts.dayCloseMode`. Under `manual` (the default) a manager closes it, as before.
// Under `auto` today's day offers no Close day — it says when it rolls over by itself — and a day that closed at its
// rollover offers **Sign off** to the manager who has read its report, then says who signed it off.

import type { DayCloseReview } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, TextField, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useRefusalText } from './refusal';
import { Notice } from '../../ui';
import { localDateLabel } from './days-and-shifts';
import { useBusinessDayStatus, useCloseBusinessDay, useSignOffBusinessDay } from './queries';

export interface DayCloseControls {
  /** Close day, or the closed badge — for the header's action slot. */
  readonly action: ReactNode;
  /** The closed banner, a refused close, or the shifts still open — above the report. */
  readonly notices: ReactNode;
  /** The confirmation dialog; render it anywhere. */
  readonly dialog: ReactNode;
}

export function useDayCloseControls({ review, organizationId, venueId }: { readonly review: DayCloseReview; readonly organizationId: string; readonly venueId: string }): DayCloseControls {
  const { t } = useTranslation('console');
  const refusalText = useRefusalText();
  const format = useVenueFormatter();
  const { show } = useToast();
  const close = useCloseBusinessDay(organizationId, venueId);
  const signOff = useSignOffBusinessDay(organizationId, venueId);
  const status = useBusinessDayStatus(organizationId, venueId).data;
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [confirming, setConfirming] = useState(false);
  const [signing, setSigning] = useState(false);
  const [note, setNote] = useState('');
  const dayLabel = localDateLabel(review.businessDay, format.locale);
  // Today's day at an automatic venue rolls over by itself: nobody closes it (ADR-0029).
  const rollsByItself = !review.closed && status?.dayCloseMode === 'auto' && status.businessDay === review.businessDay;
  const autoClosed = review.closed && review.closeMode === 'auto';
  const rollsAt = status === undefined ? '' : format.time(status.rollsOverAt);

  function submit(): void {
    setProblem(undefined);
    close.mutate(
      { businessDay: review.businessDay },
      {
        onSuccess: () => {
          setConfirming(false);
          show({ tone: 'ok', title: t('shifts.dayClosedToast', { day: dayLabel }) });
        },
        // The server names the open shift and its cashier when a shift blocks the close (F-OPS-06).
        onError: (err) => {
          setConfirming(false);
          setProblem(refusalText(err, t('shifts.error.dayClose')));
        },
      },
    );
  }

  function submitSignOff(): void {
    setProblem(undefined);
    signOff.mutate(
      { businessDay: review.businessDay, ...(note.trim() === '' ? {} : { note: note.trim() }) },
      {
        onSuccess: () => {
          setSigning(false);
          show({ tone: 'ok', title: t('shifts.signOff.done', { day: dayLabel }) });
        },
        onError: (err) => {
          setSigning(false);
          setProblem(refusalText(err, t('shifts.signOff.error')));
        },
      },
    );
  }

  const action = autoClosed ? (
    review.signedOffAt === null ? (
      <Button variant="primary" size="sm" busy={signOff.isPending} onClick={() => setSigning(true)}>
        {t('shifts.signOff.action')}
      </Button>
    ) : (
      <Badge tone="ok" dot>
        {t('shifts.signOff.signedBadge')}
      </Badge>
    )
  ) : review.closed ? (
    <Badge tone="ok" dot>
      {t('shifts.dayClosed')}
    </Badge>
  ) : rollsByItself ? (
    <Badge tone="neutral" dot>
      {t('shifts.signOff.closesAt', { time: rollsAt })}
    </Badge>
  ) : (
    <Button variant="primary" size="sm" busy={close.isPending} disabled={!review.allShiftsClosed} onClick={() => setConfirming(true)}>
      {t('shifts.closeDay')}
    </Button>
  );

  const closedAt = review.closedAt === null ? dayLabel : format.dateTime(review.closedAt, { time: 'short' });
  const notices = (
    <>
      {autoClosed ? (
        <Notice
          tone={review.signedOffAt === null ? 'warn' : 'ok'}
          role="status"
          title={t('shifts.signOff.autoClosed', { at: closedAt })}
        >
          {review.signedOffAt === null ? t('shifts.signOff.awaiting') : t('shifts.signOff.signedOff', { at: format.dateTime(review.signedOffAt, { time: 'short' }) })}
        </Notice>
      ) : review.closed ? (
        <Notice tone="ok" role="status" title={t('shifts.review.closedBanner', { at: closedAt })}>
          {t('shifts.review.closedBannerHint')}
        </Notice>
      ) : null}
      {problem !== undefined ? (
        <Notice tone="danger" role="alert" title={t('shifts.review.closeRefused')}>
          {problem}
        </Notice>
      ) : null}
      {rollsByItself ? <Notice tone="info">{t('shifts.signOff.autoOpen', { time: rollsAt })}</Notice> : null}
      {!review.closed && !rollsByItself && !review.allShiftsClosed ? <Notice tone="warn">{t('shifts.openShiftsBlock', { count: review.openShiftCount })}</Notice> : null}
    </>
  );

  const dialog = (
    <>
      <Dialog
        open={confirming}
        onOpenChange={(open) => (close.isPending ? undefined : setConfirming(open))}
        title={t('shifts.review.confirm.title', { day: dayLabel })}
        description={t('shifts.review.confirm.body')}
        footer={
          <>
            <Button variant="secondary" disabled={close.isPending} onClick={() => setConfirming(false)}>
              {t('shifts.review.confirm.cancel')}
            </Button>
            <Button variant="primary" busy={close.isPending} onClick={submit}>
              {t('shifts.closeDay')}
            </Button>
          </>
        }
      >
        <ul className="console-dayclose-confirm">
          <li>{t('shifts.review.confirm.record')}</li>
          <li>{t('shifts.review.confirm.voids')}</li>
          <li>{t('shifts.review.confirm.summary')}</li>
        </ul>
      </Dialog>
      <Dialog
        open={signing}
        onOpenChange={(open) => (signOff.isPending ? undefined : setSigning(open))}
        title={t('shifts.signOff.title', { day: dayLabel })}
        description={t('shifts.signOff.body')}
        footer={
          <>
            <Button variant="secondary" disabled={signOff.isPending} onClick={() => setSigning(false)}>
              {t('shifts.signOff.cancel')}
            </Button>
            <Button variant="primary" busy={signOff.isPending} onClick={submitSignOff}>
              {t('shifts.signOff.action')}
            </Button>
          </>
        }
      >
        <TextField label={t('shifts.signOff.note')} showOptional description={t('shifts.signOff.noteHint')} maxLength={2000} value={note} onChange={(event) => setNote(event.target.value)} />
      </Dialog>
    </>
  );

  return { action, notices, dialog };
}
